/**
 * Marks on the notes themselves: articulation, stems, beams, ties, grace notes and cues.
 *
 * Everything here works on a selection rather than on one event, because that is how the
 * marks are used -- a passage is staccato, a phrase is tied, a line of quavers is beamed
 * together -- and answers with one command, so the whole passage is one undo step.
 *
 * Marks that hang off notes rather than being part of them -- dynamics, words, slurs and
 * hairpins -- are in `marks-attached.ts`, and the signs that ask for notes nobody wrote --
 * ornaments, fermatas, arpeggios, tremolo -- are in `marks-ornaments.ts`. Grace notes,
 * which move time about, are in `marks-grace.ts`, and ties, which are put on and taken off
 * by the document's own rule for following them, are in `marks-ties.ts`. All are re-exported
 * from here, so a caller has one namespace for "what is written on the music" and does not
 * have to know which shape a given mark happens to have.
 */

import type { Articulation, BeamState, Score, StemDirection } from "../document/types";
import type { Command } from "../commands/types";
import { selectedLocations, selectedNotes } from "./selection";
import type { NoteLocation, Selection } from "./selection";

export * from "./marks-attached";
export * from "./marks-ornaments";
export * from "./marks-grace";
export * from "./marks-ties";

/**
 * Add an articulation to the selection, or take it off again.
 *
 * It is a toggle over the whole selection rather than per note: if every selected note
 * already has the mark the passage loses it, and otherwise the passage gains it. Toggling
 * each note separately would make a mixed selection alternate stripes.
 */
export function articulate(
  score: Score,
  selection: Selection,
  articulation: Articulation,
): Command | string {
  const found = selectedNotes(score, selection);
  if (found.length === 0) return "no note is selected";
  const everywhere = found.every((at) => at.event.articulations.includes(articulation));
  return {
    kind: "batch",
    label: everywhere ? `no ${articulation}` : articulation,
    commands: found.map((at) => ({
      kind: "setEventProps" as const,
      eventId: at.event.id,
      props: {
        articulations: everywhere
          ? at.event.articulations.filter((held) => held !== articulation)
          : [...at.event.articulations, articulation],
      },
    })),
  };
}

/** Take every articulation off the selection. */
export function clearArticulations(score: Score, selection: Selection): Command | string {
  const found = selectedNotes(score, selection).filter((at) => at.event.articulations.length > 0);
  if (found.length === 0) return "there are no articulations to take off";
  return {
    kind: "batch",
    label: "clear articulations",
    commands: found.map((at) => ({
      kind: "setEventProps" as const,
      eventId: at.event.id,
      props: { articulations: [] },
    })),
  };
}

/** Point the stems of the selection up, down, or back the way the engraver wants them. */
export function stem(
  score: Score,
  selection: Selection,
  direction: StemDirection,
): Command | string {
  const found = selectedNotes(score, selection);
  if (found.length === 0) return "no note is selected";
  return {
    kind: "batch",
    label: `stems ${direction}`,
    commands: found.map((at) => ({
      kind: "setEventProps" as const,
      eventId: at.event.id,
      props: { stem: direction },
    })),
  };
}

/**
 * Say how the selection beams.
 *
 * `"auto"` hands the decision back to the metre, which is what the renderer works out for
 * itself; the other states are the manual override an engraver reaches for when the metre's
 * answer is not the phrasing.
 */
export function beam(score: Score, selection: Selection, state: BeamState): Command | string {
  const found = selectedNotes(score, selection);
  if (found.length === 0) return "no note is selected";
  if (state === "auto") {
    return { kind: "batch", label: "beam automatically", commands: props(found, "auto") };
  }
  if (found.length < 2 && state !== "none") return "beaming a passage needs two notes";
  const commands =
    state === "none"
      ? props(found, "none")
      : found.map((at, index) => ({
          kind: "setEventProps" as const,
          eventId: at.event.id,
          props: { beam: edge(index, found.length) },
        }));
  return { kind: "batch", label: state === "none" ? "unbeam" : "beam together", commands };
}

function props(found: readonly NoteLocation[], state: BeamState): Command[] {
  return found.map((at) => ({
    kind: "setEventProps",
    eventId: at.event.id,
    props: { beam: state },
  }));
}

function edge(index: number, count: number): BeamState {
  if (index === 0) return "begin";
  return index === count - 1 ? "end" : "continue";
}

/**
 * Print the selection as a cue, or take it back into the part's own music.
 *
 * Rests are cued along with the notes, because a quoted passage that does not fill its bar
 * is finished with small rests rather than with the player's own. It is a toggle over the
 * whole selection, as an articulation is: a passage that is already cued throughout comes
 * back, and anything else becomes a cue.
 */
export function cue(score: Score, selection: Selection): Command | string {
  const found = selectedLocations(score, selection);
  if (found.length === 0) return "nothing is selected";
  const everywhere = found.every((at) => at.event.cue);
  return {
    kind: "batch",
    label: everywhere ? "no cue" : "cue",
    commands: found.map((at) => ({
      kind: "setEventProps" as const,
      eventId: at.event.id,
      props: { cue: !everywhere },
    })),
  };
}
